import ExpandMore from "@mui/icons-material/ExpandMore";
import PersonAdd from "@mui/icons-material/PersonAdd";
import Send from "@mui/icons-material/Send";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import {
  Button,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuList,
} from "@mui/material";
import { useState } from "react";
import { Row } from "./index";
import { ChangeAssigneeDialog } from "./ChangeAssigneeDialog";
import { ChangeStatusDialog } from "./ChangeStatusDialog";
import { SendToReviewersDialog } from "./SendToReviewersDialog";
import Delete from "@mui/icons-material/Delete";
import { DeleteSubmissionsConfirmationDialog } from "./DeleteSubmissionsConfirmationDialog";

type SubmissionBulkActionsMenuProps = {
  submissions: Row[];
  onClearSelections?: () => void;
};

export function SubmissionBulkActionsMenu({
  submissions,
  onClearSelections,
}: SubmissionBulkActionsMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [showChangeAssigneeDialog, setShowChangeAssigneeDialog] =
    useState(false);
  const [showChangeStatusDialog, setShowChangeStatusDialog] = useState(false);
  const [showSendToReviewersDialog, setShowSendToReviewersDialog] =
    useState(false);
  const [
    showDeleteSubmissionsConfirmationDialog,
    setShowDeleteSubmissionsConfirmationDialog,
  ] = useState(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenChangeAssignee = () => {
    handleClose();
    setShowChangeAssigneeDialog(true);
  };

  const handleOpenChangeStatus = () => {
    handleClose();
    setShowChangeStatusDialog(true);
  };

  const handleOpenSendToReviewers = () => {
    handleClose();
    setShowSendToReviewersDialog(true);
  };

  const handleOpenDeleteSubmissionsConfirmation = () => {
    handleClose();
    setShowDeleteSubmissionsConfirmationDialog(true);
  };

  const handleSuccess = () => {
    onClearSelections?.();
  };

  return (
    <>
      <Button
        id="bulk-actions-button"
        aria-controls={open ? "bulk-actions-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        endIcon={<ExpandMore />}
        variant="contained"
        color="secondary"
        disabled={submissions.length === 0}
        sx={{ textTransform: "uppercase" }}
      >
        Bulk Actions
      </Button>
      <Menu
        id="bulk-actions-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          "aria-labelledby": "bulk-actions-button",
        }}
      >
        <MenuList>
          <MenuItem onClick={handleOpenChangeStatus}>
            <ListItemIcon>
              <SwapHoriz fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change status...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenChangeAssignee}>
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText>Change assignee(s)...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenSendToReviewers}>
            <ListItemIcon>
              <Send fontSize="small" />
            </ListItemIcon>
            <ListItemText>Send to reviewers...</ListItemText>
          </MenuItem>
          <MenuItem onClick={handleOpenDeleteSubmissionsConfirmation}>
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete selected submissions...</ListItemText>
          </MenuItem>
        </MenuList>
      </Menu>

      <ChangeStatusDialog
        open={showChangeStatusDialog}
        onClose={() => setShowChangeStatusDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <ChangeAssigneeDialog
        open={showChangeAssigneeDialog}
        onClose={() => setShowChangeAssigneeDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <SendToReviewersDialog
        open={showSendToReviewersDialog}
        onClose={() => setShowSendToReviewersDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />

      <DeleteSubmissionsConfirmationDialog
        open={showDeleteSubmissionsConfirmationDialog}
        onClose={() => setShowDeleteSubmissionsConfirmationDialog(false)}
        submissions={submissions}
        onSuccess={handleSuccess}
      />
    </>
  );
}
